import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';

import { MonthStatusEnum } from '../enums/booking-month-status.enum';
import { EntityTypeEnum } from '../enums/entity-type.enum';
import { EventTypeEnum } from '../enums/event-type.enum';
import { FileTypeEnum } from '../enums/file-type.enum';
import { MealChoicesEnum } from '../enums/meal.enum';
import { RoleEnum } from '../enums/role.enum';
import { IClassType } from '../interfaces/class-types.interface';
import { IEnum } from '../interfaces/enum.interface';
import { IAvailableEventsDTOIn, IBookedEventsByMonth, IEventDTOIn, IMonthlyEventData } from '../interfaces/event.interface';
import { IFile, IFileElement, IFileElementDTOIn } from '../interfaces/file-element.interface';
import { ICustodyDTOIn, ICustodyItem, IGardeCalendarDate, IMonthCustody } from '../interfaces/garde.interface';
import { IClass } from '../interfaces/groups/class.interface';
import { IEntity, IEntityDTOIn } from '../interfaces/groups/entity.interface';
import { IGroup } from '../interfaces/groups/group.interface';
import { ISchoolYear } from '../interfaces/groups/school-year.interface';
import { ISchool } from '../interfaces/groups/school.interface';
import { IBookedHomeworkSchoolDTOIn, IHomeworkSchoolSetting } from '../interfaces/homework-school.interface';
import { IBookedMealDTOIn, IMealSetting } from '../interfaces/meal.interface';
import { IAttachedFile, IMessage, IMessageDTO } from '../interfaces/messaging.interface';
import { IAddressDTO } from '../interfaces/users/address.interface';
import { IAdmin, IAdminDTOIn } from '../interfaces/users/administrator.interface';
import { IContact } from '../interfaces/users/contact.interface';
import { IEmployee } from '../interfaces/users/employee.interface';
import { IParent } from '../interfaces/users/parent.interface';
import { IRelative } from '../interfaces/users/relative.interface';
import { IStudentAddress } from '../interfaces/users/student-address.interface';
import { IStudent } from '../interfaces/users/student.interface';
import { ILightUser, ILightUserDTO, IUser, IUserDTOIn } from '../interfaces/users/user.interface';
import { CustomPricingPipe } from '../pipes';
import { AuthState } from '../store/state/auth.state';
import { EnumsState } from '../store/state/enums.state';
import { HomeworkSchoolState } from '../store/state/homework-school.state';
import { MealsState } from '../store/state/meals.state';
import { SchoolYearState } from '../store/state/school-year.state';
import { UsersState } from '../store/state/users.state';
import { applyCustodyModel } from '../utils/calendar-custodies.utils';
import { getMonthTrad, isDate1BeforeDate2, parseDDMMYYYToDate } from '../utils/date.utils';
import { getEntityType } from '../utils/role.utils';

export const monthIndexArray: number[] = [7, 8, 9, 10, 11, 0, 1, 2, 3, 4, 5, 6];

@Injectable({
    providedIn: 'root',
})
export class MappersService {
    constructor(private store: Store, private translate: TranslateService, private pricingPipe: CustomPricingPipe) {}

    public castIUserToIEmployee(user: IUser): IEmployee {
        if (user) {
            return {
                id: user.id,
                lastname: user.lastname,
                firstname: user.firstname,
                gender: user.gender,
                type: EntityTypeEnum.EMPLOYEE,
                lang: user.lang,
                isActive: user.isActive,

                displayName: user.displayName,
                email: user.email,
                address: user.address,
                homeNumber: user.homeNumber,
                mobileNumber: user.mobileNumber,
                role: user.role,
                settings: user.settings,
                educationLevel: user.educationLevel,
                schoolIds: user.schoolIds,
                accessLevel: this.getAccessLevel(user.role),
            };
        }
        return null;
    }

    public castIUserToIParent(user: IUser): IParent {
        if (user) {
            return {
                id: user.id,
                lastname: user.lastname,
                firstname: user.firstname,
                gender: user.gender,
                type: EntityTypeEnum.PARENT,
                lang: user.lang,
                isActive: user.isActive,
                email: user.email,
                address: user.address,
                homeNumber: user.homeNumber,
                mobileNumber: user.mobileNumber,
                nationalNumber: user.nationalNumber,
                childrenIds: user.childrenIds,
                roles: user.role,
                settings: user.settings,
                accessLevel: 8,
                relativeIds: user.relativeIds,
            };
        }
        return null;
    }

    public castIUserToIStudent(user: IUser): IStudent {
        if (user) {
            return {
                id: user.id,
                lastname: user.lastname,
                firstname: user.firstname,
                gender: user.gender,
                type: EntityTypeEnum.STUDENT,
                lang: user.lang,
                preferredAddressId: user.preferredAddressId,
                parent_addresses: user?.parent_addresses,
                isActive: user.isActive,
                settings: user.settings,
                nationalNumber: user.nationalNumber,
                birthdate: user.birthdate,
                schoolId: user.schoolId,
                classId: user.classId,
                rateType: user.rateType,
                discount: user.discount,
                addresses: this.getStudentAddresses(user),
                parentsIds: user.parentsIds,
                relativeIds: user.relativeIds,
                accessLevel: 9,
                educationLevel: user.educationLevel,
                qrCodePath: user.qrCodePath,
            };
        }
        return null;
    }

    public castIUserToIAdmin(user: IUser): IAdmin {
        if (user) {
            return {
                id: user.id,
                lastname: user.lastname,
                firstname: user.firstname,
                email: user.email,
                type: EntityTypeEnum.ADMINISTRATOR,
                isActive: user.isActive,
                role: user.role,
                isAdmin: true,
                accessLevel: 1,
            };
        }
        return null;
    }

    public castIUserToIRelative(user: IUser): IRelative {
        if (user) {
            return {
                id: user.id,
                lastname: user.lastname,
                firstname: user.firstname,
                gender: user.gender,
                type: EntityTypeEnum.RELATIVE,
                email: user.email,
                homeNumber: user.homeNumber,
                mobileNumber: user.mobileNumber,
                lang: user.lang,
                address: user.address,
                parentRelatedToId: user.parentRelatedToId,
                studentId: user.studentId,
                relativeType: user.relativeType,
                parentId: user.parentId,
                accessLevel: this.getEntityAccessLevel(EntityTypeEnum.RELATIVE),
            };
        }
        return null;
    }

    public castIUserToIContact(user: IUser): IContact {
        if (user) {
            return {
                id: user?.id,
                gender: user?.gender,
                lastname: user?.lastname,
                firstname: user?.firstname,
                name: user?.name,
                contactType: user?.contactType,
                type: EntityTypeEnum?.CONTACT,
                email: user?.email,
                homeNumber: user?.homeNumber,
                mobileNumber: user?.mobileNumber,
                lang: user?.lang,
                address: user?.address,
                schoolId: user?.schoolId,
                website: user?.website,
                educationLevel: user.educationLevel,
            };
        }
        return null;
    }

    public castIEntityToISchool(entity: IEntity): ISchool {
        if (entity) {
            return {
                id: entity.id,
                name: entity.name,
                educationLevel: entity.educationLevel,
                isActive: entity.isActive,
                type: EntityTypeEnum.SCHOOL,
                schedules: entity.schedules,
                educationType: entity.educationType,
                email: entity.email,
                homeNumber: entity.homeNumber,
                mobileNumber: entity.mobileNumber,
                website: entity.website,
                vatNumber: entity.vatNumber,
                bankAccount: entity.bankAccount,
                employeeIds: entity.employeeIds,
                address: entity.address,
                settings: entity.settings,
                accessLevel: this.getEntityAccessLevel(EntityTypeEnum.SCHOOL),
                stripeSettings: entity.stripeSettings,
                schoolYearId: entity.schoolYearId,
            };
        }
        return null;
    }

    public castIEntityToIClass(entity: IEntity): IClass {
        if (entity) {
            return {
                id: entity.id,
                name: entity.name,
                educationLevel: entity.educationLevel,
                isActive: entity.isActive,
                type: EntityTypeEnum.CLASS,
                schedules: entity.schedules,
                schoolYear: entity.schoolYear,
                classType: entity.classType,
                teacherIds: entity.teacherIds,
                supervisorIds: entity.supervisorIds,
                studentIds: entity.studentIds,
                schoolId: entity.schoolId,
                accessLevel: this.getEntityAccessLevel(EntityTypeEnum.CLASS),
                titularIds: entity.titularIds,
                schoolClassTypeId: entity.schoolClassTypeId,
            };
        }
        return null;
    }

    public castIEntityToIGroup(entity: IEntity): IGroup {
        if (entity) {
            return {
                id: entity.id,
                name: entity.name,
                educationLevel: entity.educationLevel,
                isActive: entity.isActive,
                type: EntityTypeEnum.GROUP,
                schoolYear: entity.schoolYear,
                memberIds: entity.memberIds,
                schoolId: entity.schoolId,
                autoGenerated: entity.autoGenerated,
                accessLevel: this.getEntityAccessLevel(EntityTypeEnum.GROUP),
                users: entity.users,
            };
        }
        return null;
    }

    public castIUserToILightUser(user: IUser): ILightUser {
        return {
            id: user.id,
            email: user.email,
            firstname: user.firstname,
            lastname: user.lastname,
            displayName: user.displayName,
            name: user.name,
            role: user.role,
            nbrChilds: user.childrenIds?.length,
            nbrParents: user.parentsIds?.length,
            gender: user.gender,
            isActive: user.isActive,
            type: user.type,
            educationLevel: user.educationLevel,
            parentId: user.parentId,
            relativeType: user.relativeType,
        };
    }

    public fromIUserDTOInToIUser(userDTO: IUserDTOIn): IUser {
        return {
            id: userDTO.id,
            lastname: userDTO.lastname,
            firstname: userDTO.firstname,
            gender: this.getGender(userDTO.gender_id),
            type: this.getUserTypeFromRole(userDTO),
            isActive: userDTO.is_active,
            isAdmin: userDTO?.is_admin || false,
            lang: userDTO?.lang,
            displayName: userDTO?.display_name,
            email: userDTO?.email,
            address: userDTO?.address,
            homeNumber: userDTO?.home_number,
            mobileNumber: userDTO?.mobile_number,
            role: this.getRoles(userDTO.role_ids),
            settings: userDTO?.user_setting || userDTO.student_setting,
            nationalNumber: userDTO?.national_number,
            educationLevel: this.getEducationLevels(userDTO.education_level_ids),
            schoolIds: userDTO?.school_ids,
            childrenIds: userDTO?.child_ids,
            birthdate: userDTO?.birthdate,
            schoolId: userDTO?.school_id,
            classId: userDTO?.school_class_id,
            rateType: this.getRateTypes(userDTO?.rate_type_id),
            discount: userDTO?.discount ? +userDTO?.discount : null,
            parentsIds: userDTO?.parents_ids,
            accessLevel: this.getAccessLevelFromRolesIds(userDTO.role_ids),
            relativeIds: userDTO?.relative_ids,
            relativeType: this.getRelativeType(userDTO?.relative_type_id),
            studentId: userDTO?.student_id,
            parentRelatedToId: userDTO?.parent_id,
            qrCodePath: userDTO?.qr_code_path,
            preferredAddressId: userDTO?.preferred_address_id,
            parent_addresses: userDTO?.parent_addresses,
            parentId: userDTO?.parent_id,
            hasHomeworkOrders: userDTO.order_homework_in_active_month,
            hasMealOrders: userDTO.order_meal_in_active_month,
            //CONTACTS FIELDS
            website: userDTO?.website,
            name: userDTO?.name,
            contactType: this.getContactType(userDTO?.contact_type_id),
        };
    }

    public fromIAdminDTOInToIAdmin(adminDTO: IAdminDTOIn): IAdmin {
        return {
            id: adminDTO.id,
            firstname: adminDTO?.firstname,
            lastname: adminDTO?.lastname,
            email: adminDTO.email,
            isActive: adminDTO.is_active,
            isAdmin: adminDTO.is_admin,
            role: this.getRoles(adminDTO.role_ids),
            type: this.getUserTypeFromRole(adminDTO),
            accessLevel: 1,
        };
    }

    public fromIEntityDTOInToIEntity(entityDTO: IEntityDTOIn): IEntity {
        return {
            id: entityDTO?.id,
            name: entityDTO.name,
            educationLevel: this.getEducationLevels(
                entityDTO.education_level_ids ? entityDTO.education_level_ids : entityDTO.education_level_id
            ),
            isActive: entityDTO.is_active,
            type: entityDTO?.email ? EntityTypeEnum.SCHOOL : entityDTO?.teacher_ids ? EntityTypeEnum.CLASS : EntityTypeEnum.GROUP,
            schedules: entityDTO?.schedules,
            schoolYear: this.getSchoolYear(entityDTO?.school_year_id),
            educationType: this.getEducationTypes(entityDTO?.education_type_id),
            email: entityDTO?.email,
            autoGenerated: entityDTO.auto_generated,
            homeNumber: entityDTO?.home_number,
            mobileNumber: entityDTO?.mobile_number,
            website: entityDTO?.website,
            vatNumber: entityDTO?.vat_number,
            bankAccount: entityDTO?.bank_account,
            employeeIds: entityDTO?.employee_ids,
            address: entityDTO?.address,
            settings: entityDTO?.school_setting,
            classType: this.getClassType(entityDTO?.school_class_type_id),
            teacherIds: entityDTO?.teacher_ids,
            supervisorIds: entityDTO?.supervisor_ids,
            studentIds: entityDTO?.student_ids,
            schoolId: entityDTO?.school_id,
            memberIds: entityDTO?.user_ids,
            stripeSettings: entityDTO?.stripe_setting,
            accessLevel: this.getEntityAccessLevel(
                entityDTO?.email ? EntityTypeEnum.SCHOOL : entityDTO?.teacher_ids ? EntityTypeEnum.CLASS : EntityTypeEnum.GROUP
            ),
            titularIds: entityDTO?.titular_ids,
            schoolYearId: entityDTO.school_year_id,
            schoolClassTypeId: entityDTO.school_class_type_id,
            users: entityDTO?.users,
        };
    }

    public fromIMessageDTOtoIMessage(msg: IMessageDTO): IMessage {
        return {
            id: msg.id,
            sender: {
                id: '' + msg.sender.id,
                displayName: msg.sender.display_name ? msg.sender.display_name : (msg.sender.firstname ? msg.sender.firstname + ' ' + msg.sender.lastname : msg.sender.email),
                entityType: getEntityType(msg.sender.entity_type),
            },
            sendedMessageRecipients: msg.recipients,
            sendedMessageCopyRecipients: msg.copy_recipients,
            subject: msg.subject,
            message: msg.message,
            date: new Date(msg.created_at) || new Date(msg.deleted_at) || new Date(),
            important: msg.is_important,
            attachedFile:
                msg.files?.length > 0
                    ? msg.files.map((f: IAttachedFile) => ({
                          file_path: f.file_path,
                          name: f.name,
                      }))
                    : [],
            viewed: msg.is_read,
            educationLevelId: msg.sender.education_level_ids,
        };
    }

    public fromIMessageDTOArraytoIMessage(messagesDTO: IMessageDTO[]): IMessage[] {
        const messages: IMessage[] = [];
        messagesDTO.forEach((msg: IMessageDTO) => {
            messages.push(this.fromIMessageDTOtoIMessage(msg));
        });
        return messages;
    }

    public fromLightUserDTOToLightUser(lightUserDTO: ILightUserDTO): ILightUser {
        return {
            id: lightUserDTO.id,
            email: lightUserDTO?.email,
            firstname: lightUserDTO?.firstname,
            lastname: lightUserDTO?.lastname,
            displayName: lightUserDTO?.display_name,
            name: lightUserDTO?.name,
            role: lightUserDTO.role_ids ? this.getRoles(lightUserDTO?.role_ids) : null,
            nbrChilds: lightUserDTO?.nbr_childs,
            nbrParents: lightUserDTO?.nbr_parents,
            gender: this.getGender(lightUserDTO?.gender_id),
            isActive: lightUserDTO?.is_active,
            type: this.getUserTypeFromRole(lightUserDTO),
            educationLevel: this.getEducationLevels(lightUserDTO?.education_level_ids),
            parentId: lightUserDTO.parent_id,
            relativeType: this.getRelativeType(lightUserDTO?.relative_type_id),
        };
    }

    public fromIFileElementDTOInToIFileElement(fileElementDTOIn: IFileElementDTOIn): IFileElement {
        return {
            id: fileElementDTOIn.id,
            name: fileElementDTOIn.name,

            folders: fileElementDTOIn.folders,
            items: fileElementDTOIn.items.map((file: IFile) => ({ ...file, file_type: this.getFileType(file.file_path) })),

            employeeIds: fileElementDTOIn.employee_ids,
            schoolClassIds: fileElementDTOIn.school_class_ids,
            groupIds: fileElementDTOIn.group_ids,
        };
    }

    public fromIFileToInContextIFile(file: IFile): IFile {
        return {
            ...file,
            file_type: this.getFileType(file.file_path),
        };
    }

    // CUSTODY MAPPING
    public fromCustodyDTOInToMonthCustody(custody: ICustodyDTOIn, parentsLight?: ILightUser[]): IMonthCustody[] {
        const connectedUser: IUser = this.store.selectSnapshot(AuthState.user);
        let currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
        if (currentUser.type !== EntityTypeEnum.STUDENT) {
            currentUser = this.store.selectSnapshot(UsersState.userToEdit);
        }

        let parents: IUser[] = [];
        if (connectedUser.type === EntityTypeEnum.PARENT) {
            parents = [
                connectedUser,
                {
                    id: currentUser?.parentsIds.find((id: number) => id !== connectedUser.id) || 0,
                    firstname: 'Non communiqué',
                    lastname: '',
                },
            ];
        } else {
            parents = parentsLight.map((lightUser: ILightUser) => ({
                id: lightUser.id,
                firstname: lightUser.firstname,
                lastname: lightUser.lastname,
            }));
            if (parents.length === 0) {
                parents = [
                    { id: currentUser.parentsIds[0], firstname: 'Parent 1', lastname: '' },
                    { id: currentUser.parentsIds[1], firstname: 'Parent 2', lastname: '' },
                ];
            }
        }
        const selectedParentId: number = connectedUser.type === EntityTypeEnum.PARENT ? connectedUser.id : currentUser.parentsIds[0];

        let custodyData: IGardeCalendarDate[] = [];
        if (custody.student_custody.type && custody.student_custody.type !== 4) {
            custodyData = applyCustodyModel(custody.student_custody.type, parents, custody.student_custody.parent_one_id); // here to change parent id ? custody.student_custody.parent_one_id
        }

        return monthIndexArray.map((monthIndex: number) => ({
            monthIndex,
            monthName: getMonthTrad(monthIndex),
            custody: custody.custody_items.length
                ? custody.custody_items
                      .filter((c: ICustodyItem) => !!c?.date && c?.date?.getMonth() === monthIndex)
                      .map((item: ICustodyItem) => ({
                          date: new Date(item.date.setHours(12)),
                          parent_id: item.parent_id,
                          colorClass: selectedParentId === item.parent_id ? ' -blue' : ' -pink',
                          firstname: parents.find((parent: IUser) => parent.id === item.parent_id || parent.id === 0).firstname,
                      }))
                : custodyData.filter((c: IGardeCalendarDate) => c.date.getMonth() === monthIndex),
        }));
    }

    // EVENTS MAPPING
    public fromAvailableEventsToEventsData(availableEvents: IAvailableEventsDTOIn): IMonthlyEventData[] {
        return monthIndexArray.map((monthIndex: number) => ({
            month: monthIndex,
            monthName: getMonthTrad(monthIndex),
            meals: availableEvents?.meals
                ?.filter((meal: IEventDTOIn) => parseDDMMYYYToDate(meal.date).getMonth() === monthIndex)
                .map((meal: IEventDTOIn) => ({
                    id: meal.id,
                    date: parseDDMMYYYToDate(meal.date),
                    eventType: EventTypeEnum.MEAL,
                })),
            homeworkSchool: availableEvents?.homeworks
                ?.filter((homework: IEventDTOIn) => parseDDMMYYYToDate(homework.date).getMonth() === monthIndex)
                .map((homework: IEventDTOIn) => ({
                    id: homework.id,
                    date: parseDDMMYYYToDate(homework.date),
                    eventType: EventTypeEnum.HOMEWORK,
                })),
            status: this.getMonthStatus(
                monthIndex,
                availableEvents.meals ? EventTypeEnum.MEAL : EventTypeEnum.HOMEWORK,
                availableEvents.meals
                    ? availableEvents.meals?.filter((meal: IEventDTOIn) => parseDDMMYYYToDate(meal.date).getMonth() === monthIndex).length >
                          0
                    : availableEvents.homeworks
                    ? availableEvents.homeworks?.filter(
                          (homework: IEventDTOIn) => parseDDMMYYYToDate(homework.date).getMonth() === monthIndex
                      ).length > 0
                    : false
            ),
        }));
    }

    // MEALS MAPPING
    public fromBookedMealsToBookedMealsByMonth(bookedMeals: IBookedMealDTOIn[]): IBookedEventsByMonth[] {
        return monthIndexArray.map((monthIndex: number) => ({
            monthNumber: monthIndex,
            monthName: getMonthTrad(monthIndex),
            bookedMeals:
                bookedMeals?.length > 0
                    ? bookedMeals
                          .filter((bookedMeal: IBookedMealDTOIn) => parseDDMMYYYToDate(bookedMeal.meal.date).getMonth() === monthIndex)
                          .map((meal: IBookedMealDTOIn) => ({
                              id: meal.id,
                              meal_choices: meal.meal_choices,
                              color:
                                  meal.meal_choices[0] === MealChoicesEnum.TOAST
                                      ? '#90A4AE'
                                      : meal.meal_choices[0] === MealChoicesEnum.SOUP
                                      ? '#AED581'
                                      : meal.meal_choices[0] === MealChoicesEnum.HOT_MEAL
                                      ? '#E57373'
                                      : '',
                              student: meal.student,
                              parents: meal.parents,
                              meal: {
                                  id: meal?.meal?.id,
                                  date: parseDDMMYYYToDate(meal.meal.date),
                                  eventType: EventTypeEnum.MEAL,
                                  amount: meal.meal?.amount,
                              },
                              educationLevelId: meal.education_level_id,
                              paymentStatus: meal.payment_status,
                          }))
                          .sort((a: any, b: any) => a.meal.date - b.meal.date)
                    : [],
        }));
    }

    // HOMEWORK SCHOOL MAPPING
    public fromBookedHomeworkSchoolToBookedHomeworkSchoolByMonth(
        bookedHomeworksSchool: IBookedHomeworkSchoolDTOIn[]
    ): IBookedEventsByMonth[] {
        return monthIndexArray.map((monthIndex: number) => ({
            monthNumber: monthIndex,
            monthName: getMonthTrad(monthIndex),
            bookedHomeworkSchool:
                bookedHomeworksSchool?.length > 0
                    ? bookedHomeworksSchool
                          .filter(
                              (bookedHomeworkSchool: IBookedHomeworkSchoolDTOIn) =>
                                  parseDDMMYYYToDate(bookedHomeworkSchool.homework.date).getMonth() === monthIndex
                          )
                          .map((homeworkSchool: IBookedHomeworkSchoolDTOIn) => ({
                              id: homeworkSchool.id,
                              color: '#64b5f6',
                              student: homeworkSchool.student,
                              parents: homeworkSchool.parents,
                              homeworkSchool: {
                                  id: homeworkSchool?.homework?.id,
                                  date: parseDDMMYYYToDate(homeworkSchool.homework.date),
                                  amount: homeworkSchool.homework?.amount,
                                  eventType: EventTypeEnum.HOMEWORK,
                                  homeworkType: homeworkSchool.homework?.homework_type,
                              },
                              educationLevelId: homeworkSchool.education_level_id,
                              paymentStatus: homeworkSchool.payment_status,
                          }))
                          .sort((a: any, b: any) => a.homeworkSchool.date - b.homeworkSchool.date)
                    : [],
        }));
    }

    public getMonthStatus(monthIndex: number, eventType: EventTypeEnum, hasEvents: boolean): MonthStatusEnum {
        const schoolYear: ISchoolYear = this.store.selectSnapshot(SchoolYearState.getCurrentSchoolYear);
        const monthYear: number = monthIndex < 7 ? schoolYear?.end_year : schoolYear?.start_year;
        const currentUser: IUser = this.store.selectSnapshot(AuthState.currentUser);
        let openFrom: Date;
        let openUntil: Date;
        if (eventType === EventTypeEnum.MEAL) {
            if (currentUser.type === EntityTypeEnum.EMPLOYEE) {
                const mealSettings: IMealSetting[] = this.store.selectSnapshot(MealsState.schoolMealsSettings);
                openFrom = new Date(
                    new Date(monthYear, monthIndex, 1).setDate(
                        mealSettings[0].min_days > mealSettings[1].min_days ? -mealSettings[0].min_days : -mealSettings[1].min_days
                    )
                );
                openUntil = new Date(
                    new Date(monthYear, monthIndex, 1).setDate(
                        mealSettings[0].max_days < mealSettings[1].max_days ? -mealSettings[0].max_days + 1 : -mealSettings[1].max_days + 1
                    )
                );
            } else {
                const currentUserEducationLevelId: number = currentUser.educationLevel[0]?.id;
                const mealSettings: IMealSetting = this.store
                    .selectSnapshot(MealsState.schoolMealsSettings)
                    .find((setting: IMealSetting) => setting.education_level_id === currentUserEducationLevelId);
                openFrom = new Date(new Date(monthYear, monthIndex, 1).setDate(-mealSettings.min_days));
                openUntil = new Date(new Date(monthYear, monthIndex, 1).setDate(-mealSettings.max_days + 1));
            }
        } else if (eventType === EventTypeEnum.HOMEWORK) {
            const homeworkSetting: IHomeworkSchoolSetting = this.store.selectSnapshot(HomeworkSchoolState.schoolHomeworkSchoolSettings);
            openFrom = new Date(new Date(monthYear, monthIndex, 1).setDate(-homeworkSetting.min_days));
            openUntil = new Date(new Date(monthYear, monthIndex, 1).setDate(-homeworkSetting.max_days + 1));
        }

        const now: Date = new Date();

        if (isDate1BeforeDate2(now, openFrom)) {
            return MonthStatusEnum.INACTIVE;
        } else if (isDate1BeforeDate2(openUntil, now)) {
            return MonthStatusEnum.CLOSED;
        } else if (isDate1BeforeDate2(openFrom, now) && isDate1BeforeDate2(now, openUntil) && hasEvents) {
            return MonthStatusEnum.OPEN;
        } else if (isDate1BeforeDate2(openFrom, now) && isDate1BeforeDate2(now, openUntil) && !hasEvents) {
            return MonthStatusEnum.INACTIVE;
        }
        return MonthStatusEnum.CLOSED;
    }

    private getGender(genderId: number): IEnum {
        const gendersEnums: IEnum[] = this.store.selectSnapshot(EnumsState.getGendersEnum);
        return gendersEnums.find((elem: IEnum) => elem.id === genderId);
    }

    private getRoles(rolesIds: number[]): IEnum[] {
        const rolesEnums: IEnum[] = this.store.selectSnapshot(EnumsState.getRolesEnum);
        if (rolesIds) {
            return rolesIds.map((roleId: number) => rolesEnums.find((elem: IEnum) => elem.id === roleId));
        } else {
            return null;
        }
    }

    private getEducationLevels(edLevelIds: number[] | number): IEnum[] {
        if (edLevelIds) {
            const edLevelsEnums: IEnum[] = this.store.selectSnapshot(EnumsState.getEducationLevelsEnum);
            if (Array.isArray(edLevelIds)) {
                return edLevelIds
                    .map((edLevelId: number) => edLevelsEnums.find((elem: IEnum) => elem.id === edLevelId))
                    .sort((a: IEnum, b: IEnum) => b.id - a.id);
            } else {
                return [edLevelsEnums.find((edLevelEnum: IEnum) => edLevelEnum.id === edLevelIds)];
            }
        } else {
            return null;
        }
    }

    private getEducationTypes(edTypeId: number): IEnum {
        const edTypesEnums: IEnum[] = this.store.selectSnapshot(EnumsState.getEducationTypesEnum);
        return edTypesEnums.find((elem: IEnum) => elem.id === edTypeId);
    }

    private getUserTypeFromRole(userDTO: IUserDTOIn): EntityTypeEnum {
        if (userDTO?.relative_type_id) {
            return EntityTypeEnum.RELATIVE;
        }
        if (userDTO?.role_ids) {
            const employeeRoleIdKeys: number[] = [2, 3, 4, 5, 6, 7];
            return employeeRoleIdKeys.includes(userDTO.role_ids[0] || userDTO.role_ids[1])
                ? EntityTypeEnum.EMPLOYEE
                : userDTO.role_ids.includes(1)
                ? EntityTypeEnum.ADMINISTRATOR
                : userDTO.role_ids.includes(8)
                ? EntityTypeEnum.PARENT
                : EntityTypeEnum.STUDENT;
        } else {
            if (userDTO?.contact_type_id) {
                return EntityTypeEnum.CONTACT;
            }
            return EntityTypeEnum.STUDENT;
        }
    }

    private getSchoolYear(schoolYearId: number): ISchoolYear {
        if (schoolYearId) {
            const schoolYears: ISchoolYear[] = this.store.selectSnapshot(SchoolYearState.getAllSchoolYear);
            return schoolYears.find((schoolYear: ISchoolYear) => schoolYear.id === schoolYearId);
        } else {
            return null;
        }
    }

    private getClassType(classTypeId: number): IClassType {
        if (classTypeId) {
            const classTypes: IClassType[] = this.store.selectSnapshot(EnumsState.getClassTypesEnum);
            return classTypes.find((classType: IClassType) => classType.id === classTypeId);
        } else {
            return null;
        }
    }

    private getRateTypes(rateTypeId: number): IEnum {
        if (rateTypeId) {
            const rateTypes: IEnum[] = this.store.selectSnapshot(EnumsState.rateTypesEnum);
            return rateTypes.find((rateType: IEnum) => rateType.id === rateTypeId);
        } else {
            return null;
        }
    }

    private getContactType(contactTypeId: number): IEnum {
        if (contactTypeId) {
            const contactTypes: IEnum[] = this.store.selectSnapshot(EnumsState.contactTypesEnum);
            return contactTypes.find((contactType: IEnum) => contactType.id === contactTypeId);
        } else {
            return null;
        }
    }

    private getRelativeType(relativeTypeId: number): IEnum {
        if (relativeTypeId) {
            const relativeTypes: IEnum[] = this.store.selectSnapshot(EnumsState.relativeTypesEnum);
            return relativeTypes.find((relativeType: IEnum) => relativeType.id === relativeTypeId);
        } else {
            return null;
        }
    }

    private getAccessLevel(roles: IEnum[]): number {
        if (roles) {
            const mainRole: IEnum = roles[0];
            switch (mainRole?.type as RoleEnum) {
                case RoleEnum.ADMINISTRATOR:
                    return 1;
                case RoleEnum.ALDERMAN:
                    return 2;
                case RoleEnum.DIRECTOR:
                    return 3;
                case RoleEnum.ASSISTANT:
                    return 4;
                case RoleEnum.SECRETARY:
                    return 5;
                case RoleEnum.TEACHER:
                    return 6;
                case RoleEnum.SUPERVISOR:
                    return 7;
                case RoleEnum.PARENT:
                    return 8;
                case RoleEnum.STUDENT:
                    return 9;
            }
        }
        return null;
    }

    private getAccessLevelFromRolesIds(roleIds: number[]): number {
        return this.getAccessLevel(this.getRoles(roleIds));
    }

    private getEntityAccessLevel(entityType: EntityTypeEnum): number {
        switch (entityType) {
            case EntityTypeEnum.RELATIVE:
                return 5;
            case EntityTypeEnum.SCHOOL:
                return 1;
            case EntityTypeEnum.CLASS:
                return 2;
            case EntityTypeEnum.GROUP:
                return 2;
            default:
                return 1;
        }
    }

    private getStudentAddresses(userDTOIn: IUser): IStudentAddress {
        if (userDTOIn.type === EntityTypeEnum.STUDENT) {
            const parents: IParent[] = this.store.selectSnapshot(UsersState.getAllParents);
            const addressOne: IAddressDTO = parents.find((p: IParent) => p.id === userDTOIn.settings.parent_one.parent_id)?.address;
            const addressTwo: IAddressDTO = parents.find((p: IParent) => p.id === userDTOIn.settings.parent_two.parent_id)?.address;
            return {
                parentOne: addressOne,
                parentTwo: addressTwo,
                other: userDTOIn.address
                    ? userDTOIn.address
                    : userDTOIn.addresses && userDTOIn.addresses.other
                    ? userDTOIn.addresses.other
                    : null,
            };
        }
        return null;
    }

    private getFileType(path: string): FileTypeEnum {
        const extension: string = path.split('.')[1];
        switch (extension) {
            case 'pdf':
                return FileTypeEnum.PDF;
            case 'zip':
                return FileTypeEnum.ZIP;
            case 'doc':
            case 'docx':
                return FileTypeEnum.WORD;
            case 'xls':
            case 'xlsx':
                return FileTypeEnum.EXCEL;
            case 'ppt':
            case 'pptx':
                return FileTypeEnum.PPT;
            default:
                return FileTypeEnum.IMG;
        }
    }
}
